import { useMemo, useState, type ReactNode } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import type { MatchListValue } from '@/components/tree-dnd/tree-dnd-types';
import type { MatchListOption } from '@/components/tree-dnd/tree-dnd-conditions/mock-options';
import { useI18nContext } from '@/i18n/i18n-react';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { OptionButtonGroup } from '@/components/ui/option-button-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';

interface MatchListControlProps {
  value: MatchListValue;
  onChange: (value: MatchListValue) => void;
  options: MatchListOption[];
  allowAll?: boolean;
  placeholder?: string;
  renderItem?: (option: MatchListOption) => ReactNode;
  variant?: 'compact' | 'expanded';
  isLoading?: boolean;
  matchModeHelp?: {
    any: string;
    all: string;
  };
}

function MatchListOptions({
  options,
  value,
  onToggleId,
  renderItem,
  noResultsLabel,
  searchPlaceholder,
  listClassName,
  className,
}: {
  options: MatchListOption[];
  value: MatchListValue;
  onToggleId: (id: string) => void;
  renderItem?: (option: MatchListOption) => ReactNode;
  noResultsLabel: string;
  searchPlaceholder: string;
  listClassName?: string;
  className?: string;
}) {
  return (
    <Command className={className}>
      <CommandInput placeholder={searchPlaceholder} />
      <CommandList className={listClassName}>
        <CommandEmpty>{noResultsLabel}</CommandEmpty>
        <CommandGroup>
          {options.map((option) => {
            const selected = value.ids.includes(option.id);
            return (
              <CommandItem
                key={option.id}
                value={`${option.label} ${option.description ?? ''}`}
                onSelect={() => onToggleId(option.id)}
              >
                <Check
                  className={cn(
                    'mr-2 size-4',
                    selected ? 'opacity-100' : 'opacity-0'
                  )}
                />
                {renderItem ? (
                  renderItem(option)
                ) : (
                  <div className="flex min-w-0 items-center gap-2">
                    {option.previewImageUrl !== undefined ? (
                      option.previewImageUrl ? (
                        <img
                          src={option.previewImageUrl}
                          alt=""
                          className="size-8 shrink-0 rounded object-cover"
                        />
                      ) : (
                        <div
                          className="size-8 shrink-0 rounded bg-muted"
                          aria-hidden
                        />
                      )
                    ) : null}
                    <div className="flex min-w-0 flex-col">
                      <span
                        className={cn(
                          'truncate text-sm',
                          option.unavailable && 'text-muted-foreground'
                        )}
                      >
                        {option.label}
                      </span>
                      {option.description ? (
                        <span className="truncate text-xs text-muted-foreground">
                          {option.description}
                        </span>
                      ) : null}
                    </div>
                  </div>
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}

export function MatchListControl({
  value,
  onChange,
  options,
  allowAll = true,
  placeholder,
  renderItem,
  variant = 'compact',
  isLoading = false,
  matchModeHelp,
}: MatchListControlProps) {
  const { LL } = useI18nContext();
  const [open, setOpen] = useState(false);
  const resolvedPlaceholder = placeholder ?? LL.audiences.filters.select();

  const selectedLabels = useMemo(() => {
    const byId = new Map(options.map((option) => [option.id, option]));
    return value.ids
      .map((id) => byId.get(id)?.label)
      .filter((label): label is string => Boolean(label));
  }, [options, value.ids]);

  const triggerLabel =
    selectedLabels.length === 0
      ? resolvedPlaceholder
      : selectedLabels.length <= 2
        ? selectedLabels.join(', ')
        : LL.audiences.filters.selectedCount({ count: selectedLabels.length });

  const toggleId = (id: string) => {
    const ids = value.ids.includes(id)
      ? value.ids.filter((item) => item !== id)
      : [...value.ids, id];
    onChange({ ...value, ids });
  };

  const matchLabels = {
    any: LL.audiences.filters.matchAny(),
    all: LL.audiences.filters.matchAll(),
  };

  const matchOptions = [
    { value: 'any' as const, label: matchLabels.any },
    { value: 'all' as const, label: matchLabels.all },
  ];

  if (variant === 'expanded') {
    return (
      <div className="flex flex-col gap-4">
        {allowAll ? (
          <div className="space-y-2">
            <OptionButtonGroup
              value={value.match}
              options={matchOptions}
              onChange={(match) => onChange({ ...value, match })}
              ariaLabel={LL.audiences.filters.matchMode()}
            />
            {matchModeHelp ? (
              <p className="text-sm text-muted-foreground">
                {value.match === 'all' ? matchModeHelp.all : matchModeHelp.any}
              </p>
            ) : null}
          </div>
        ) : null}

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 rounded-lg border py-10 text-sm text-muted-foreground">
            <Spinner className="size-5" />
            <span>{LL.audiences.filters.products.loading()}</span>
          </div>
        ) : (
          <MatchListOptions
            options={options}
            value={value}
            onToggleId={toggleId}
            renderItem={renderItem}
            noResultsLabel={LL.audiences.filters.noResults()}
            searchPlaceholder={LL.audiences.filters.search()}
            className="overflow-hidden rounded-lg border"
            listClassName="max-h-[min(24rem,calc(100vh-16rem))]"
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {allowAll ? (
        <ButtonGroup
          aria-label={LL.audiences.filters.matchMode()}
          className="[&_button]:h-7 [&_button]:rounded-sm [&_button]:px-2 [&_button]:text-xs [&_button]:font-semibold [&_button]:uppercase"
        >
          {matchOptions.map((option) => {
            const isActive = value.match === option.value;
            return (
              <Button
                key={option.value}
                type="button"
                variant={isActive ? 'secondary' : 'ghost'}
                className={cn(
                  !isActive &&
                    'text-muted-foreground hover:bg-muted/50 hover:text-muted-foreground'
                )}
                onClick={() => onChange({ ...value, match: option.value })}
                aria-pressed={isActive}
              >
                {option.label}
              </Button>
            );
          })}
        </ButtonGroup>
      ) : null}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="compact"
            role="combobox"
            aria-expanded={open}
            className="h-7 min-w-36 justify-between font-normal"
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronsUpDown className="ml-1 size-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0">
          <MatchListOptions
            options={options}
            value={value}
            onToggleId={toggleId}
            renderItem={renderItem}
            noResultsLabel={LL.audiences.filters.noResults()}
            searchPlaceholder={LL.audiences.filters.search()}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
